import { useEffect, useRef, useState } from "react";
import { ChevronDown, Paperclip, Plus, Upload, X } from "lucide-react";
import AddCustomerLead from "./AddCustomerLead";
import { getLeadDropdownMetadata } from "../api/auth"; 
import api from "../api/api"; // Centralized Axios instance configuration block
import Toast, { useToast } from "../components/Toast";

const allSections = [
  "Professional",
  "Point of Contact",
  // "Pre-Sales",
  "General Information",
  "Requirement",
  "Attachments",
];

const createEmptyContact = () => ({
  name: "",
  email: "",
  mobileNumber: "",
  designation: "",
});

const inputClassName =
  "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white";
const labelClassName = "mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500";

// Formats a numeric string using the Indian digit-grouping system (e.g. 1234567 -> "12,34,567")
function formatIndianCurrency(value) {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);

  // Keep only digits and a single decimal point
  const sanitized = stringValue.replace(/[^0-9.]/g, "");
  if (sanitized === "") return "";

  const [integerPartRaw, ...decimalParts] = sanitized.split(".");
  const decimalPart = decimalParts.length ? decimalParts.join("") : undefined;

  // Strip leading zeros (but keep a single zero if that's all there is)
  const integerPart = integerPartRaw.replace(/^0+(?=\d)/, "") || "0";

  let formattedInteger;
  if (integerPart.length <= 3) {
    formattedInteger = integerPart;
  } else {
    const lastThree = integerPart.slice(-3);
    const remaining = integerPart.slice(0, -3);
    formattedInteger =
      remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
  }

  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
}

// Strips comma separators back out, returning the raw numeric string for state/submission
function unformatCurrency(value) {
  if (value === null || value === undefined) return "";
  return String(value).replace(/,/g, "");
}

function Field({ label, children }) {
  return (
    <div className="block">
      <span className={labelClassName}>{label}</span>
      {children}
    </div>
  );
}

const EMPTY_FIELD_SUGGESTIONS = {
  address: [],
  city: [],
  state: [],
  pincode: [],
  cin: [],
  gstin: [],
};

// Text input with a suggestion list. Unlike a native datalist, the full
// list is shown when the field is focused even if it is already filled
// (e.g. auto-filled from the customer), and is only filtered once the
// person starts typing. Free text is still allowed.
function SuggestInput({ value, options = [], onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const needle = String(value || "").trim().toLowerCase();
  const visible =
    typed && needle
      ? options.filter((option) => option.toLowerCase().includes(needle))
      : options;
  const hasOptions = options.length > 0;

  return (
    <div
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
          setTyped(false);
        }
      }}
    >
      <div className="relative">
        <input
          className={`${inputClassName} ${hasOptions ? "pr-10" : ""}`}
          autoComplete="off"
          placeholder={placeholder}
          value={value}
          onFocus={() => { setTyped(false); setOpen(true); }}
          onChange={(event) => { setTyped(true); setOpen(true); onChange(event.target.value); }}
          onKeyDown={(event) => { if (event.key === "Escape") setOpen(false); }}
        />
        {hasOptions && (
          <button
            type="button"
            tabIndex={-1}
            aria-label={`Show ${placeholder} suggestions`}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => { setTyped(false); setOpen((previous) => !previous); }}
          >
            <ChevronDown size={16} />
          </button>
        )}
      </div>
      {open && hasOptions && visible.length > 0 && (
        <div className="absolute z-30 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
          {visible.map((option) => (
            <button
              key={option}
              type="button"
              className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => { onChange(option); setOpen(false); setTyped(false); }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchableSelect({ value, options, onChange, placeholder, addNewLabel, onAddNew }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => String(option.value) === String(value));
  const visible = options.filter((option) =>
    option.label.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
          setQuery("");
        }
      }}
    >
      <div className="relative">
        <input
          className={`${inputClassName} pr-10`}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          value={open ? query : selected?.label || ""}
          placeholder={placeholder}
          onFocus={() => { setQuery(""); setOpen(true); }}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); onChange(""); }}
          onKeyDown={(event) => {
            if (event.key === "Escape") { setOpen(false); setQuery(""); }
            if (event.key === "Enter" && open && visible.length === 1) {
              event.preventDefault();
              onChange(visible[0].value);
              setOpen(false);
              setQuery("");
            }
          }}
        />
        <button
          type="button"
          aria-label={`Show ${placeholder} options`}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500"
          onClick={() => { setQuery(""); setOpen((previous) => !previous); }}
        >
          <ChevronDown size={16} />
        </button>
      </div>
      {open && (
        <div className="absolute z-30 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
          {visible.map((option) => (
            <button key={option.value} type="button" className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50" onClick={() => { onChange(option.value); setOpen(false); setQuery(""); }}>
              {option.label}
            </button>
          ))}
          {visible.length === 0 && <p className="px-3 py-2 text-sm text-slate-500">No matching options</p>}
          {onAddNew && (
            <button type="button" className="block w-full border-t border-slate-100 px-3 py-2 text-left text-sm font-medium text-blue-700 hover:bg-blue-50" onClick={() => { setOpen(false); setQuery(""); onAddNew(); }}>
              {addNewLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function AddLeadModal({ onClose }) {
  const { toast, showToast, hideToast } = useToast();
  const [activeSection, setActiveSection] = useState("Professional");
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // Dynamic Options state from Database
  const [dropdownOptions, setDropdownOptions] = useState({
    customers: [],
    sources: [],
    levels: [],
    priorities: [],
    statuses: [],
    presalesUsers: [],
  });

  const [form, setForm] = useState({
    customerId: "",       // Dynamic customer ID choice reference
    newCustomerName: "",  // Text field fallback
    sourceId: "",         // maps to ld_source
    address: "",
    city: "",
    state: "",
    pincode: "",
    cin: "",
    gstin: "",
    selectedExistingPocs: [],
contacts: [createEmptyContact()],
    levelId: "",          // maps to ld_level_id
    priorityId: "",       // maps to ld_priority_id
    presalesId: "",       // maps to ld_presales_id
    oem: "",
    oemAccountManager: "",
    product: "",
    amount: "",
    expectedClosure: "",
    drNumber: "",
    drExpiry: "",
    remarks: "",
    statusId: "",         // maps to ld_status_id    // maps to ld_status_remark
    estimatedBudget: "",  // maps to ld_estimated_budget (used as Amount)
    requirementNotes: "", // triggers your custom /<int:lead_id>/notes endpoint
    attachments: [],
  });

  const sectionRefs = useRef({});
  const contentRef = useRef(null);
  const selectedCustomer = dropdownOptions.customers.find(
  (c) => String(c.cust_id) === String(form.customerId)
);

const existingPocs = selectedCustomer?.point_of_contacts || [];

  // Suggestions for Address / City / State / Pincode / CIN / GSTIN: the
  // customer's own record plus every opportunity already linked to them.
  const [fieldSuggestions, setFieldSuggestions] = useState(EMPTY_FIELD_SUGGESTIONS);

  // Fetch Lookup parameters from Database on mounted initialization
  const fetchMetadata = async () => {
    try {
      setLoadingMeta(true);
      const response = await getLeadDropdownMetadata();
      if (response.data && response.data.success) {
        setDropdownOptions((prev) => ({ ...prev, ...(response.data.data || {}) }));
      }
    } catch (error) {
      console.error("Failed loading backend metadata parameters:", error);
    } finally {
      setLoadingMeta(false);
    }
  };

  useEffect(() => {
    fetchMetadata();
  }, []);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      for (const section of allSections) {
        const element = sectionRefs.current[section];
        if (!element) continue;

        const top = element.offsetTop - container.offsetTop;
        const bottom = top + element.offsetHeight;
        const scrollPosition = container.scrollTop + 120;

        if (scrollPosition >= top && scrollPosition < bottom) {
          setActiveSection(section);
          break;
        }
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
  if (isAddingCustomer || !form.customerId) {
    return;
  }

  const customer = dropdownOptions.customers.find(
    (c) => String(c.cust_id) === String(form.customerId)
  );

  if (!customer) return;

  setForm((prev) => ({
    ...prev,
    address: customer.cust_address || "",
    city: customer.cust_city || "",
    state: customer.cust_state || "",
    pincode: customer.cust_pincode || "",
    cin: customer.cust_cin || "",
    gstin: customer.cust_gst || "",
  }));
}, [form.customerId, dropdownOptions.customers, isAddingCustomer]);

  // Load the suggestion lists whenever an existing customer is selected.
  useEffect(() => {
    if (isAddingCustomer || !form.customerId) {
      setFieldSuggestions(EMPTY_FIELD_SUGGESTIONS);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await api.get(`/leads/address-suggestions/${form.customerId}`);
        if (!cancelled && res.data?.success) {
          setFieldSuggestions({ ...EMPTY_FIELD_SUGGESTIONS, ...(res.data.suggestions || {}) });
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load field suggestions", err);
          setFieldSuggestions(EMPTY_FIELD_SUGGESTIONS);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form.customerId, isAddingCustomer]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateContact = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, contactIndex) =>
        contactIndex === index ? { ...contact, [key]: value } : contact
      ),
    }));
  };

  const addContact = () => {
    setForm((prev) => ({
      ...prev,
      contacts: [...prev.contacts, createEmptyContact()],
    }));
  };

  const removeContact = (index) => {
    setForm((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((_, contactIndex) => contactIndex !== index),
    }));
  };

  const scrollTo = (section) => {
    sectionRefs.current[section]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const setCustomerMode = (shouldAddCustomer) => {
    setIsAddingCustomer(shouldAddCustomer);
    setForm((prev) => ({
      ...prev,
      customerId: shouldAddCustomer ? "" : prev.customerId,
      newCustomerName: shouldAddCustomer ? prev.newCustomerName : "",
    }));
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList ?? []);
    if (!files.length) return;
    setForm((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));
  };

  const removeAttachment = (index) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, fileIndex) => fileIndex !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!isAddingCustomer && !form.customerId) {
      showToast("Please choose a valid customer parameter.", "warning");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();

      // Relational IDs & Dynamic customer triggers
      formData.append("isAddingCustomer", isAddingCustomer ? "true" : "false");
      formData.append("customerId", form.customerId);
      formData.append("newCustomerName", form.newCustomerName);
      
      formData.append("sourceId", form.sourceId);
      formData.append("levelId", form.levelId);
      formData.append("priorityId", form.priorityId);
      formData.append("statusId", form.statusId);
      formData.append("presalesId", form.presalesId);
      formData.append(
  "requirementNotes",
  form.requirementNotes
);

      // Raw descriptive values
      formData.append("address", form.address);
      formData.append("city", form.city);
      formData.append("state", form.state);
      formData.append("pincode", form.pincode);
      formData.append("cin", form.cin);
      formData.append("gstin", form.gstin);
      formData.append("estimatedBudget", form.estimatedBudget);
      formData.append("oem", form.oem);
      formData.append("oemAccountManager", form.oemAccountManager);
      formData.append("product", form.product);
      formData.append("amount", form.amount);
      formData.append("expectedClosure", form.expectedClosure);
      formData.append("drNumber", form.drNumber);
      formData.append("drExpiry", form.drExpiry);
      formData.append("remarks", form.remarks);
      formData.append(
  "selectedExistingPocs",
  JSON.stringify(form.selectedExistingPocs)
);
      // JSON Array Stringification for POCs
      formData.append("contacts", JSON.stringify(form.contacts));

      // Iterative File payloads
      form.attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      // Step 1: Fire main request to generate the opportunity record
      const response = await api.post("/leads/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

     if (response.data.success) {
    onClose("Opportunity successfully created!");
}else {
        showToast(response.data.message || "An unexpected processing dropout occurred.", "error");
      }
    } catch (error) {
      console.error("Submission layout failure details:", error);
      showToast(error.response?.data?.message || "Failed to establish sync with the remote data layout.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <Toast toast={toast} onClose={hideToast} />
      <div className="absolute inset-0 bg-slate-950/40" onClick={() => onClose()}></div>

      <div className="relative ml-auto flex h-full w-full max-w-6xl bg-white shadow-2xl">
        <aside className="hidden w-[220px] border-r border-slate-200 bg-slate-50 p-4 lg:block">
          <div className="space-y-2">
            {allSections.map((section) => (
              <button
                key={section}
                type="button"
                onClick={() => scrollTo(section)}
                className={`w-full border-l-2 px-3 py-2 text-left text-sm transition ${
                  activeSection === section
                    ? "border-blue-600 bg-blue-50 font-semibold text-blue-700"
                    : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
                }`}
              >
                {section}
              </button>
            ))}
          </div>
        </aside>

        <div className="flex flex-1 flex-col">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-6 py-3 sm:py-4">
            <div>
              <h2 className="text-lg font-semibold text-blue-700">Add Opportunity</h2>
              <p className="text-sm text-slate-500">Capture configuration options dynamically from data profiles.</p>
            </div>

            <button
              type="button"
              onClick={() => onClose()}
              className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 cursor-pointer"
            >
              <X size={20} />
            </button>
          </div>

          <div ref={contentRef} className="flex-1 space-y-10 overflow-auto px-4 sm:px-6 py-6">
            {loadingMeta ? (
              <div className="py-20 text-center text-sm font-medium text-slate-500">
                Fetching parameters dynamically from database options...
              </div>
            ) : (
              <>
                {/* Section 1: Professional */}
                <section ref={(element) => (sectionRefs.current.Professional = element)} className="space-y-5">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Professional</h3>
                  </div>

                  <p className="text-xs text-slate-500 -mt-3">
                    Selecting an existing customer auto-fills Address, City, State, Pincode, CIN and GSTIN below from their record — click a field to pick from this customer's earlier details, or edit them for this opportunity. Changes here apply only to this opportunity, not to the customer's own saved details.
                  </p>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Customer Name">
                      {isAddingCustomer ? (
                        <div className="space-y-2">
                          <input
                            className={inputClassName}
                            placeholder="Enter new customer name"
                            value={form.newCustomerName}
                            onChange={(event) => updateField("newCustomerName", event.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => setCustomerMode(false)}
                            className="text-sm font-medium text-blue-700 transition hover:text-blue-800 cursor-pointer"
                          >
                            Choose Existing Database Customer
                          </button>
                        </div>
                      ) : (
                        <SearchableSelect
                          value={form.customerId}
                          options={dropdownOptions.customers.map((c) => ({ value: c.cust_id, label: c.cust_name || "" }))}
                          onChange={(value) => updateField("customerId", value)}
                          placeholder="Type or select customer"
                          addNewLabel="➕ Add Brand New Customer"
                          onAddNew={() => setShowAddCustomerModal(true)}
                        />
                      )}
                    </Field>

                    <Field label="Source">
                      <SearchableSelect value={form.sourceId} options={dropdownOptions.sources.map((src) => ({ value: src.code_id, label: src.code_short_description || "" }))} onChange={(value) => updateField("sourceId", value)} placeholder="Type or select source" />
                    </Field>

                    <Field label="Address">
                      <SuggestInput
                        placeholder="Street, area, landmark"
                        value={form.address}
                        options={fieldSuggestions.address}
                        onChange={(value) => updateField("address", value)}
                      />
                    </Field>

                    <Field label="City">
                      <SuggestInput
                        placeholder="Enter city"
                        value={form.city}
                        options={fieldSuggestions.city}
                        onChange={(value) => updateField("city", value)}
                      />
                    </Field>

                    <Field label="State">
                      <SuggestInput
                        placeholder="Enter state"
                        value={form.state}
                        options={fieldSuggestions.state}
                        onChange={(value) => updateField("state", value)}
                      />
                    </Field>

                    <Field label="Pincode">
                      <SuggestInput
                        placeholder="Enter pincode"
                        value={form.pincode}
                        options={fieldSuggestions.pincode}
                        onChange={(value) => updateField("pincode", value)}
                      />
                    </Field>
                    <Field label="CIN">
                     <SuggestInput
                        placeholder="Enter CIN"
                        value={form.cin}
                        options={fieldSuggestions.cin}
                        onChange={(value) => updateField("cin", value)}
                      />
                    </Field>
                    <Field label="GSTIN">
                      <SuggestInput
                        placeholder="Enter GSTIN"
                        value={form.gstin}
                        options={fieldSuggestions.gstin}
                        onChange={(value) => updateField("gstin", value)}
                      />
                    </Field>
                  </div>
                </section>

                {/* Section 2: Point of Contact */}
                <section ref={(element) => (sectionRefs.current["Point of Contact"] = element)} className="space-y-5">
                  <div className="flex flex-col items-start gap-5">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Point of Contact</h3>
                      <p className="text-sm text-slate-500">Maps structural sub-entries to your relational contacts profiles.</p>
                    </div>
                    {!isAddingCustomer && form.customerId && existingPocs.length > 0 && (
  <div className="w-full min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="mb-4">
      <h4 className="text-sm font-semibold text-slate-800">
        Existing Point(s) of Contact
      </h4>

      <p className="mt-1 text-xs text-slate-500">
        Select one or more existing contacts to associate with this lead.
      </p>
    </div>

    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {existingPocs.map((poc) => {
        const checked = form.selectedExistingPocs.includes(
          String(poc.poc_id)
        );

        return (
          <label
            key={poc.poc_id}
            className={`flex min-w-0 cursor-pointer items-start gap-3 overflow-hidden rounded-xl border p-4 transition
              ${
                checked
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
              }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                if (e.target.checked) {
                  updateField("selectedExistingPocs", [
                    ...form.selectedExistingPocs,
                    String(poc.poc_id),
                  ]);
                } else {
                  updateField(
                    "selectedExistingPocs",
                    form.selectedExistingPocs.filter(
                      (id) => id !== String(poc.poc_id)
                    )
                  );
                }
              }}
              className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />

            <div className="min-w-0 flex-1">
              <div className="break-words font-medium text-slate-800">
                {poc.poc_name}
              </div>

              {poc.poc_designation && (
                <div className="break-words text-xs text-blue-600">
                  {poc.poc_designation}
                </div>
              )}

              {poc.poc_email && (
                <div className="mt-1 break-all text-xs text-slate-500">
                  {poc.poc_email}
                </div>
              )}

              {poc.poc_contact && (
                <div className="break-all text-xs text-slate-500">
                  {poc.poc_contact}
                </div>
              )}
            </div>
          </label>
        );
      })}
    </div>
  </div>
)}

                    <button
                      type="button"
                      onClick={addContact}
                      className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-100 cursor-pointer"
                    >
                      
                      <Plus size={16} />
                      Add Contact
                    </button>
                  </div>

                  <div className="space-y-4">
                    {form.contacts.map((contact, index) => (
                      <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                        <div className="mb-4 flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-slate-700">Contact {index + 1}</h4>
                          {form.contacts.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeContact(index)}
                              className="text-sm font-medium text-rose-600 transition hover:text-rose-700 cursor-pointer"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                          <Field label="Name">
                            <input
                              className={inputClassName}
                              placeholder="Contact name"
                              value={contact.name}
                              onChange={(event) => updateContact(index, "name", event.target.value)}
                            />
                          </Field>

                          <Field label="Email">
                            <input
                              className={inputClassName}
                              type="email"
                              placeholder="name@company.com"
                              value={contact.email}
                              onChange={(event) => updateContact(index, "email", event.target.value)}
                            />
                          </Field>

                          <Field label="Mobile Number">
                            <input
                              className={inputClassName}
                              placeholder="Enter mobile number"
                              type="number"
                              value={contact.mobileNumber}
                              onChange={(event) => updateContact(index, "mobileNumber", event.target.value)}
                            />
                          </Field>

                          <Field label="Designation">
                            <input
                              className={inputClassName}
                              placeholder="Enter designation"
                              value={contact.designation}
                              onChange={(event) => updateContact(index, "designation", event.target.value)}
                            />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Pre-Sales Assignment */}
                {/* <section ref={(element) => (sectionRefs.current["Pre-Sales"] = element)} className="space-y-5">
                  <div><h3 className="text-base font-semibold text-slate-900">Pre-Sales</h3></div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Field label="Pre-Sales Individual">
                      <select
                        className={inputClassName}
                        value={form.presalesId}
                        onChange={(e) => updateField("presalesId", e.target.value)}
                      >
                        <option value="">Select Pre-Sales</option>
                        {(dropdownOptions.presalesUsers || []).map((user) => (
                          <option key={user.usr_id} value={user.usr_id}>
                            {user.usr_name}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </section> */}

                {/* Section 3: General Information */}
                <section ref={(element) => (sectionRefs.current["General Information"] = element)} className="space-y-5">
                  <div><h3 className="text-base font-semibold text-slate-900">General Information</h3></div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Field label="OEM"><input className={inputClassName} value={form.oem} placeholder="Enter OEM" onChange={(e)=>updateField("oem",e.target.value)} /></Field>

                    <Field label="OEM A/C Mgr"><input className={inputClassName} value={form.oemAccountManager} placeholder="Enter OEM Account Manager" onChange={(e)=>updateField("oemAccountManager",e.target.value)} /></Field>

                    <Field label="Product"><input className={inputClassName} value={form.product} placeholder="Enter Product" onChange={(e)=>updateField("product",e.target.value)} /></Field>

                    <Field label="Stage">
                      <SearchableSelect value={form.levelId} options={dropdownOptions.levels.map((lvl) => ({ value: lvl.code_id, label: lvl.code_short_description || "" }))} onChange={(value) => updateField("levelId", value)} placeholder="Type or select stage" />
                    </Field>

                    {/* <Field label="Priority">Priority Removed</Field> */}

                    <Field label="Amount">
                      <input
                        type="text"
                        inputMode="decimal"
                        className={inputClassName}
                        value={formatIndianCurrency(form.amount || form.estimatedBudget)}
                        placeholder="Enter Amount"
                        onChange={(e) => {
                          const rawValue = unformatCurrency(e.target.value);
                          // Guard against multiple decimal points / stray characters
                          if (rawValue !== "" && !/^\d*\.?\d*$/.test(rawValue)) return;
                          updateField("amount", rawValue);
                          updateField("estimatedBudget", rawValue);
                        }}
                      />
                    </Field>

                    <Field label="Expected Closure"><input type="date" className={inputClassName} value={form.expectedClosure} onChange={(e)=>updateField("expectedClosure",e.target.value)} /></Field>

                    <Field label="DR Number"><input className={inputClassName} value={form.drNumber} placeholder="Enter DR Number" onChange={(e)=>updateField("drNumber",e.target.value)} /></Field>

                    <Field label="DR Expiry"><input type="date" className={inputClassName} value={form.drExpiry} onChange={(e)=>updateField("drExpiry",e.target.value)} /></Field>

                    <Field label="Status">
                      <SearchableSelect value={form.statusId} options={dropdownOptions.statuses.map((sts) => ({ value: sts.code_id, label: sts.code_short_description || "" }))} onChange={(value) => updateField("statusId", value)} placeholder="Type or select status" />
                    </Field>

                    <Field label=" Remarks"><input className={inputClassName} value={form.remarks} placeholder="remarks" onChange={(e)=>updateField("remarks",e.target.value)} /></Field>
                  </div>
                </section>
                
                {/* Section 4: Requirement */}
                
                <section ref={(element) => (sectionRefs.current.Requirement = element)} className="space-y-5">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Requirement</h3>
                  </div>

                  <Field label="Notes">
                    <textarea
                      className={`${inputClassName} min-h-36 resize-y`}
                      placeholder="Describe the requirement scope, timeline, parameters..."
                      value={form.requirementNotes}
                      onChange={(event) => updateField("requirementNotes", event.target.value)}
                    />
                  </Field>
                </section>

                {/* Section 5: Attachments */}
                <section ref={(element) => (sectionRefs.current.Attachments = element)} className="space-y-5">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Attachments</h3>
                  </div>

                  <label
                    className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition ${
                      isDraggingFiles
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/60"
                    }`}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setIsDraggingFiles(true);
                    }}
                    onDragLeave={() => setIsDraggingFiles(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setIsDraggingFiles(false);
                      handleFiles(event.dataTransfer.files);
                    }}
                  >
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(event) => handleFiles(event.target.files)}
                    />

                    <div className="mb-4 rounded-full bg-blue-100 p-3 text-blue-700">
                      <Upload size={22} />
                    </div>

                    <p className="text-sm font-semibold text-slate-800">Upload Files</p>
                    <p className="mt-2 text-sm text-slate-500">or Drag &amp; Drop Files</p>
                  </label>

                  {form.attachments.length > 0 && (
                    <div className="space-y-2">
                      {form.attachments.map((file, index) => (
                        <div
                          key={`${file.name}-${index}`}
                          className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 bg-white"
                        >
                          <div className="flex items-center gap-3 overflow-hidden">
                            <Paperclip size={16} className="shrink-0 text-slate-400" />
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-slate-700">{file.name}</p>
                              <p className="text-xs text-slate-400">
                                {(file.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="text-sm font-medium text-rose-600 transition hover:text-rose-700 cursor-pointer"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>

          <div className="sticky bottom-0 flex justify-end gap-3 border-t border-slate-200 bg-white p-4">
            <button
              type="button"
              onClick={() => onClose()}
              disabled={isSubmitting || loadingMeta}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || loadingMeta}
              className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50 cursor-pointer flex items-center justify-center min-w-[80px]"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      {showAddCustomerModal && (
        <AddCustomerLead
          onClose={() => setShowAddCustomerModal(false)}
          onSaveSuccess={async () => {
            setShowAddCustomerModal(false);
            // Instantly sync data options dropdown
            await fetchMetadata();
          }}
        />
      )}
    </div>
  )
}